import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/pageHeader/PageHeader';
import Wrapper from '../../components/atoms/DirectorioWrapper';
import SidebarProcesosSeleccion from '../../components/pageComponents/SidebarProcesosSeleccion';
import SpinnerLoading from '../../components/pageComponents/SpinnerLoading';
import MOCK_POSTULACIONES from './mockDataExpanded';
import { useGetRequest } from '../../hooks/useGetRequest/useGetRequest';
import {
  getReportePostulacionesDashboardAPI,
  getReportePostulacionesDetalleAPI
} from '../../consumers/backendApisUrls';
import './ReportesPostulacionesDashboard.css';

const EVAL_TYPES = {
  psicologicas: 'Psicologicas',
  conocimientos: 'Conocimientos',
  otros: 'Otros'
};

const SUBTYPE_VALUES = {
  salamanca: 'SALAMANCA',
  dass21: 'DASS-21'
};

const CARD_CONFIG = [
  { key: 'total', title: 'Postulaciones Analizadas', accent: 'var(--rp-blue)' },
  { key: 'aprobacion', title: 'Tasa de Aprobación', accent: 'var(--rp-green)' },
  { key: 'desaprobados', title: 'Cantidad Desaprobados', accent: 'var(--rp-red)' },
  { key: 'cantidadEvaluaciones', title: 'Cantidad de Evaluaciones', accent: 'var(--rp-orange)' }
];

const ROWS_PER_PAGE = 10;
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const API_MONTH_MAP = {
  jan: 'ene',
  feb: 'feb',
  mar: 'mar',
  apr: 'abr',
  may: 'may',
  jun: 'jun',
  jul: 'jul',
  aug: 'ago',
  sep: 'sep',
  oct: 'oct',
  nov: 'nov',
  dec: 'dic',
  dic: 'dic'
};

const TIPO_EVALUACION_API_MAP = {
  [EVAL_TYPES.psicologicas]: 248801,
  [EVAL_TYPES.conocimientos]: 248802,
  [EVAL_TYPES.otros]: 248803
};

const SUBTIPO_API_MAP = {
  [SUBTYPE_VALUES.salamanca]: 1,
  [SUBTYPE_VALUES.dass21]: 2
};

const monthLabel = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-PE', { month: 'short' }).toLowerCase();
};

const resolveApiMonth = (item) => {
  const monthNumber = Number(item?.MesNumero ?? item?.mesNumero);
  if (Number.isInteger(monthNumber) && monthNumber >= 1 && monthNumber <= 12) {
    return MONTHS[monthNumber - 1];
  }

  const rawMonth = String(item?.Mes ?? item?.mes ?? '').toLowerCase().slice(0, 3);
  return API_MONTH_MAP[rawMonth] || rawMonth;
};

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-PE');
};

const isPsicologicaType = (tipoEvaluacion) => String(tipoEvaluacion || '').toLowerCase().includes('psicolog');

const formatDuracionCompacto = (segundos) => {
  if (!segundos || segundos === 0) return '0m';
  
  const secs = Math.floor(segundos);
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  
  return remainingSecs > 0 ? `${mins}m${remainingSecs}s` : `${mins}m`;
};

const unwrapApiPayload = (response) => {
  let payload = response?.data;
  let guard = 0;

  while (payload && typeof payload === 'object' && guard < 5) {
    if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
      payload = payload.data;
      guard += 1;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'Data')) {
      payload = payload.Data;
      guard += 1;
      continue;
    }

    // Algunos endpoints devuelven un sobre con una sola clave (ej: { tipoEvaluacion: { Code, Data } }).
    const keys = Object.keys(payload);
    if (keys.length === 1) {
      const singleValue = payload[keys[0]];
      if (singleValue && typeof singleValue === 'object') {
        payload = singleValue;
        guard += 1;
        continue;
      }
    }

    // Respuesta estándar de API { Code, Message, Data }.
    if (
      Object.prototype.hasOwnProperty.call(payload, 'Code') &&
      Object.prototype.hasOwnProperty.call(payload, 'Data')
    ) {
      payload = payload.Data;
      guard += 1;
      continue;
    }

    break;
  }

  return payload;
};

const toYmd = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return String(dateValue).slice(0, 10);
  }

  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, '0');
  const dd = `${date.getDate()}`.padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const mapDetalleItem = (item) => ({
  id: item?.IdPostulacionEvaluacion ?? item?.idPostulacionEvaluacion ?? item?.IdPostulacion ?? item?.idPostulacion,
  postulante: item?.Postulante ?? item?.postulante ?? '-',
  cargo: item?.Cargo ?? item?.cargo ?? '-',
  fecha: toYmd(item?.Fecha ?? item?.fecha),
  tipoEvaluacion: item?.TipoEvaluacion ?? item?.tipoEvaluacion ?? '-',
  subtipo: item?.SubtipoPsicologico ?? item?.subtipoPsicologico ?? item?.subtipo ?? '-',
  puntaje: Number(item?.NotaVigesimal ?? item?.notaVigesimal ?? item?.PuntajeObtenido ?? item?.puntajeObtenido ?? 0),
  puntajeMaximo: Number(item?.PuntajeMaximo ?? item?.puntajeMaximo ?? 20),
  resultado: item?.Resultado ?? item?.resultado ?? 'Desaprobado',
  estadoPostulacion: item?.EstadoPostulacion ?? item?.estadoPostulacion ?? '-',
  intentoNum: Number(item?.IntentosRealizados ?? item?.intentosRealizados ?? item?.intentoNum ?? 1),
  duracionMin: Number(item?.DuracionMin ?? item?.duracionMin ?? 0),
  nombreEvaluacion: item?.NombreEvaluacion ?? item?.nombreEvaluacion ?? '-'
});

const buildDashboardFromEvaluaciones = (items = []) => {
  const safeItems = Array.isArray(items) ? items : [];

  const totalPostulaciones = safeItems.length;
  const activos = safeItems.filter((item) => item?.estado === true).length;
  const inactivos = totalPostulaciones - activos;

  const porMesMap = safeItems.reduce((acc, item) => {
    const month = monthLabel(item?.fechaCreacion || item?.fechaCreacionTexto);
    if (!month) return acc;
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  const postulacionesPorMes = MONTHS
    .filter((month) => (porMesMap[month] || 0) > 0)
    .map((month) => ({ Mes: month, Cantidad: porMesMap[month] || 0 }));

  const porTipoMap = safeItems.reduce((acc, item) => {
    const tipo = item?.tipoEvaluacion || '-';
    acc[tipo] = (acc[tipo] || 0) + 1;
    return acc;
  }, {});

  const distribucionPorTipoEvaluacion = Object.entries(porTipoMap).map(([tipo, cantidad]) => ({
    TipoEvaluacion: tipo,
    Cantidad: cantidad
  }));

  const postulacionesPorEstado = [
    { EstadoPostulacion: 'Activa', Cantidad: activos },
    { EstadoPostulacion: 'Inactiva', Cantidad: inactivos }
  ];

  return {
    kpis: {
      totalPostulaciones,
      aprobados: activos,
      desaprobados: inactivos,
      tasaAprobacion: totalPostulaciones > 0 ? (activos * 100) / totalPostulaciones : 0,
      tiempoPromedioMin: 0
    },
    postulacionesPorMes,
    distribucionPorTipoEvaluacion,
    postulantesPorIntentos: [],
    postulacionesPorEstado,
    aprobadosVsDesaprobadosPorEvaluacion: [],
    tendenciaMensualAprobacion: []
  };
};

const mapEvaluacionesToDetalleItems = (items = []) => {
  const safeItems = Array.isArray(items) ? items : [];

  return safeItems.map((item) => ({
    IdPostulacionEvaluacion: item?.idEvaluacion,
    IdPostulacion: item?.idEvaluacion,
    Fecha: item?.fechaCreacion,
    Postulante: '-',
    Cargo: item?.cargo || '-',
    TipoEvaluacion: item?.tipoEvaluacion || '-',
    SubtipoPsicologico: '-',
    NotaVigesimal: 0,
    Resultado: item?.estado ? 'Activo' : 'Inactivo',
    EstadoPostulacion: item?.estado ? 'Activa' : 'Inactiva',
    IntentosRealizados: 1,
    DuracionMin: 0,
    NombreEvaluacion: item?.nombreEvaluacion || '-',
    IdEvaluacion: item?.idEvaluacion,
    PuntajeObtenido: 0,
    PuntajeMaximo: 20
  }));
};

const normalizeTipoEvaluacion = (value = '') => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw.includes('psicolog')) return EVAL_TYPES.psicologicas;
  if (raw.includes('conocimiento')) return EVAL_TYPES.conocimientos;
  if (raw.includes('otro')) return EVAL_TYPES.otros;
  return value;
};

const normalizeSubtipo = (value = '') => String(value || '').trim().toUpperCase().replace(/\s+/g, '');

const filterEvaluacionesByFilters = (items = [], filtros = {}) => {
  const safeItems = Array.isArray(items) ? items : [];

  return safeItems.filter((item) => {
    const fecha = toYmd(item?.fechaCreacion || item?.fechaCreacionTexto || item?.FechaCreacion);
    const tipo = normalizeTipoEvaluacion(item?.tipoEvaluacion || item?.TipoEvaluacion);

    const subtipoRaw =
      item?.subtipoPsicologico ||
      item?.SubtipoPsicologico ||
      item?.subtipo ||
      item?.Subtipo ||
      item?.nombreSubtipo ||
      item?.NombreSubtipo ||
      '';
    const subtipo = normalizeSubtipo(subtipoRaw);

    const fechaOk = !fecha || (fecha >= filtros.fechaInicio && fecha <= filtros.fechaFin);
    const tipoOk = filtros.tipoEvaluacion === 'TODOS' || tipo === filtros.tipoEvaluacion;

    const selectedSubtipo = normalizeSubtipo(filtros.subtipo);
    const subtipoOk = filtros.subtipo === 'TODOS' || subtipo === selectedSubtipo;

    if (filtros.subtipo !== 'TODOS' && tipo !== EVAL_TYPES.psicologicas) {
      return false;
    }

    return fechaOk && tipoOk && subtipoOk;
  });
};

const DashboardCard = ({ title, value, hint, accent }) => (
  <div className="rp-kpi-card" style={{ '--kpi-accent': accent }}>
    <span className="rp-kpi-title">{title}</span>
    <strong className="rp-kpi-value">{value}</strong>
    <small className="rp-kpi-hint">{hint}</small>
  </div>
);

const SimpleBarChart = ({ data, maxValue, colorClass }) => (
  <div className="rp-bar-chart">
    {data.map((item) => {
      const percentage = maxValue > 0 ? Math.round((item.value / maxValue) * 100) : 0;
      return (
        <div className="rp-bar-row" key={item.label}>
          <span className="rp-bar-label">{item.label}</span>
          <div className="rp-bar-track">
            <div className={`rp-bar-fill ${colorClass}`} style={{ width: `${percentage}%` }} />
          </div>
          <span className="rp-bar-value">{item.value}</span>
        </div>
      );
    })}
  </div>
);

const StackedBarChart = ({ data }) => (
  <div className="rp-stacked-bar-chart">
    {data.map((item) => {
      const total = item.approved + item.rejected;
      const approvedPercent = total > 0 ? (item.approved / total) * 100 : 0;
      const rejectedPercent = total > 0 ? (item.rejected / total) * 100 : 0;

      return (
        <div className="rp-stacked-bar-row" key={item.label}>
          <span className="rp-stacked-bar-label">{item.label}</span>
          <div className="rp-stacked-bar-container">
            <div className="rp-stacked-bar">
              {item.approved > 0 && (
                <div
                  className="rp-stacked-section green"
                  style={{ width: `${approvedPercent}%` }}
                  title={`${item.approved} aprobados`}
                >
                  {approvedPercent > 15 && <span className="rp-section-value">{item.approved}</span>}
                </div>
              )}
              {item.rejected > 0 && (
                <div
                  className="rp-stacked-section red"
                  style={{ width: `${rejectedPercent}%` }}
                  title={`${item.rejected} desaprobados`}
                >
                  {rejectedPercent > 15 && <span className="rp-section-value">{item.rejected}</span>}
                </div>
              )}
            </div>
          </div>
          <span className="rp-stacked-bar-stats">
            {total} <span className="rp-approval-rate">({Math.round(approvedPercent)}%)</span>
          </span>
        </div>
      );
    })}
  </div>
);

const TinyLineChart = ({ data }) => {
  const width = 820;
  const height = 220;
  const padding = { top: 30, right: 20, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  const values = data.map((item) => item.value).filter((v) => v > 0);
  const maxValue = Math.max(...values, 100);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;
  const stepX = chartWidth / Math.max(data.length - 1, 1);

  const points = data
    .map((item, index) => {
      const x = padding.left + index * stepX;
      const y = padding.top + chartHeight - ((item.value - minValue) / range) * chartHeight;
      return { x, y, ...item };
    })

  const pointsString = points.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <div className="rp-line-chart-wrapper" style={{ marginBottom: '20px' }}>
      <svg viewBox={`0 0 ${width} ${height}`} className="rp-line-chart" style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }} aria-hidden="true">
        {/* Grid horizontal */}
        {[0, 25, 50, 75, 100].map((val) => {
          const y = padding.top + chartHeight - ((val - minValue) / range) * chartHeight;
          return (
            <g key={`grid-${val}`}>
              <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 15} y={y + 4} fontSize="12" fill="#666" textAnchor="end">{val}%</text>
            </g>
          );
        })}

        {/* Línea */}
        <polyline points={pointsString} className="rp-line-path" fill="none" stroke="#1d4ed8" strokeWidth="3" />

        {/* Puntos con valores */}
        {points.map((point, index) => (
          <g key={`point-${index}`}>
            {/* Punto visible */}
            <circle cx={point.x} cy={point.y} r="5" fill="#1d4ed8" stroke="white" strokeWidth="2" />
            {/* Valor sobre el punto */}
            {point.value > 0 && (
              <text 
                x={point.x} 
                y={point.y - 12} 
                fontSize="13" 
                fontWeight="bold"
                fill="#1d4ed8" 
                textAnchor="middle"
              >
                {Math.round(point.value)}%
              </text>
            )}
            {/* Línea vertical desde el eje X */}
            <line x1={point.x} y1={padding.top + chartHeight} x2={point.x} y2={point.y} stroke="#1d4ed8" strokeWidth="1" opacity="0.2" />
          </g>
        ))}

        {/* Eje X */}
        <line x1={padding.left} y1={padding.top + chartHeight} x2={width - padding.right} y2={padding.top + chartHeight} stroke="#333" strokeWidth="2" />
        {/* Eje Y */}
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="#333" strokeWidth="2" />
      </svg>

      {/* Etiquetas de meses */}
      <div className="rp-line-labels" style={{ display: 'grid', gridTemplateColumns: `repeat(${data.length}, 1fr)`, marginTop: '10px', paddingLeft: padding.left, gap: '0' }}>
        {points.map((point) => (
          <span key={`label-${point.label}`} style={{ textAlign: 'center', fontSize: '12px', color: '#666', marginLeft: '-20px' }}>
            {point.label}
          </span>
        ))}
      </div>

      {/* Tabla de datos debajo */}
      <div style={{ marginTop: '20px', fontSize: '12px', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '12px' }}>
          {data.filter((item) => item.value > 0).map((item) => (
            <div key={item.label} style={{ textAlign: 'center', padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 'bold', color: '#333' }}>{item.label.toUpperCase()}</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1d4ed8' }}>
                {Math.round(item.value)}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PieChart = ({ data }) => {
  const total = Math.max(data.reduce((sum, item) => sum + item.value, 0), 1);
  const colors = ['#1d4ed8', '#10b981', '#ea580c'];

  let cumulativePercentage = 0;
  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const startAngle = (cumulativePercentage / 100) * 360;
    const endAngle = ((cumulativePercentage + percentage) / 100) * 360;
    cumulativePercentage += percentage;
    return {
      label: item.label,
      value: item.value,
      percentage,
      color: colors[index % colors.length],
      startAngle,
      endAngle
    };
  });

  return (
    <div className="rp-pie-container">
      <svg className="rp-pie-chart" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="95" fill="none" stroke="#f1f5f9" strokeWidth="2" />
        {slices.map((slice, index) => {
          const startRad = (slice.startAngle * Math.PI) / 180;
          const endRad = (slice.endAngle * Math.PI) / 180;
          const x1 = 100 + 90 * Math.cos(startRad);
          const y1 = 100 + 90 * Math.sin(startRad);
          const x2 = 100 + 90 * Math.cos(endRad);
          const y2 = 100 + 90 * Math.sin(endRad);
          const largeArc = slice.percentage > 50 ? 1 : 0;

          const pathData = `M 100 100 L ${x1} ${y1} A 90 90 0 ${largeArc} 1 ${x2} ${y2} Z`;

          return (
            <path
              key={index}
              d={pathData}
              fill={slice.color}
              stroke="white"
              strokeWidth="2"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
            />
          );
        })}
      </svg>

      <div className="rp-pie-legend">
        {slices.map((slice, index) => (
          <div key={index} className="rp-pie-legend-item">
            <div className="rp-pie-color" style={{ backgroundColor: slice.color }} />
            <div className="rp-pie-legend-text">
              <span className="rp-pie-label">{slice.label}</span>
              <span className="rp-pie-value">{slice.value} ({Math.round(slice.percentage)}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Table = ({ rows, sortConfig, onSort }) => {
  const getSortIndicator = (column) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleHeaderClick = (column) => {
    let direction = 'asc';
    if (sortConfig.column === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    onSort(column, direction);
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'nombreEvaluacion', label: 'Evaluación' },
    { key: 'postulante', label: 'Postulante' },
    { key: 'cargo', label: 'Cargo' },
    { key: 'fecha', label: 'Fecha' },
    { key: 'tipoEvaluacion', label: 'Tipo' },
    { key: 'subtipo', label: 'Subtipo' },
    { key: 'puntaje', label: 'Nota' },
    { key: 'resultado', label: 'Resultado' },
    { key: 'estadoPostulacion', label: 'Estado Postulación' },
    { key: 'duracionMin', label: 'Duración' }
  ];

  return (
    <div className="table-responsive rp-table-wrap">
      <table className="table table-hover">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => handleHeaderClick(col.key)}
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  backgroundColor: sortConfig.column === col.key ? '#f0f0f0' : undefined,
                  fontWeight: sortConfig.column === col.key ? 'bold' : undefined
                }}
                title="Haz clic para ordenar"
              >
                {col.label}{getSortIndicator(col.key)}
              </th>
            ))}
          </tr>
        </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{row.id}</td>
            <td>{row.nombreEvaluacion || '-'}</td>
            <td>{row.postulante}</td>
            <td>{row.cargo}</td>
            <td>{formatDate(row.fecha)}</td>
            <td>{row.tipoEvaluacion}</td>
            <td>{row.subtipo || '-'}</td>
            <td>{(['Aprobado', 'Desaprobado'].includes(row.resultado) && !isPsicologicaType(row.tipoEvaluacion)) ? row.puntaje : '-'}</td>
            <td>
              <span className={`rp-badge ${
                row.resultado === 'Aprobado'
                  ? 'ok'
                  : (row.resultado === 'Pendiente' ? 'obs' : 'ko')
              }`}>
                {row.resultado}
              </span>
            </td>
            <td>{row.estadoPostulacion}</td>
            <td>{formatDuracionCompacto(row.duracionMin)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const ReportesPostulacionesDashboard = () => {
  const [executeGetRequest] = useGetRequest();

  const currentYear = new Date().getFullYear();

  const [filtros, setFiltros] = useState({
    fechaInicio: `${currentYear}-01-01`,
    fechaFin: `${currentYear}-12-31`
  });

  const [busquedaEvaluacion, setBusquedaEvaluacion] = useState('');
  const [debouncedBusquedaEvaluacion, setDebouncedBusquedaEvaluacion] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ column: 'id', direction: 'desc' });

  const [dashboardData, setDashboardData] = useState(null);
  const [apiDetalleRows, setApiDetalleRows] = useState([]);
  const [apiPagination, setApiPagination] = useState({
    totalItems: 0,
    currentPage: 1,
    pageSize: ROWS_PER_PAGE,
    totalPages: 1
  });

  const [dashboardServiceFailed, setDashboardServiceFailed] = useState(false);
  const [detalleServiceFailed, setDetalleServiceFailed] = useState(false);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingDetalle, setLoadingDetalle] = useState(true);
  const [searchTriggered, setSearchTriggered] = useState(true);

  const shouldUseMockData = dashboardServiceFailed || detalleServiceFailed;
  const showInitialLoader =
    !shouldUseMockData &&
    !dashboardData &&
    apiDetalleRows.length === 0 &&
    (loadingDashboard || loadingDetalle);

  const apiFilterParams = useMemo(() => ({
    fechaInicio: filtros.fechaInicio,
    fechaFin: filtros.fechaFin,
    idTipoEvaluacion: null,
    idSubtipoPruebaPsicologica: null
  }), [filtros]);

  const filteredMockRows = useMemo(() => {
    if (!searchTriggered) return [];
    return MOCK_POSTULACIONES.filter((row) => {
      const fechaOk = row.fecha >= filtros.fechaInicio && row.fecha <= filtros.fechaFin;
      return fechaOk;
    });
  }, [filtros, searchTriggered]);

  const mockTotalPages = Math.max(1, Math.ceil(filteredMockRows.length / ROWS_PER_PAGE));

  const paginatedMockRows = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return filteredMockRows.slice(start, end);
  }, [filteredMockRows, currentPage]);

  const totalPages = shouldUseMockData
    ? mockTotalPages
    : Math.max(1, Number(apiPagination?.totalPages || 1));

  const totalResults = shouldUseMockData
    ? filteredMockRows.length
    : Number(apiPagination?.totalItems || apiDetalleRows.length);

  const sortedTableRows = useMemo(() => {
    if (!sortConfig.column) return shouldUseMockData ? paginatedMockRows : apiDetalleRows;

    const rowsToSort = shouldUseMockData ? paginatedMockRows : apiDetalleRows;
    const sorted = [...rowsToSort].sort((a, b) => {
      const aValue = a[sortConfig.column];
      const bValue = b[sortConfig.column];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, 'es-ES');
      } else {
        comparison = String(aValue).localeCompare(String(bValue), 'es-ES');
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [sortConfig, paginatedMockRows, apiDetalleRows, shouldUseMockData]);

  const tableRows = sortedTableRows;

  const visiblePages = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);

    if (end - start + 1 < windowSize) {
      start = Math.max(1, end - windowSize + 1);
    }

    const pages = [];
    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTriggered]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedBusquedaEvaluacion(busquedaEvaluacion);
    }, 400);

    return () => clearTimeout(timeout);
  }, [busquedaEvaluacion]);

  useEffect(() => {
    if (!searchTriggered) return;

    let isMounted = true;

    const loadDashboard = async () => {
      setLoadingDashboard(true);
      try {
        const response = await executeGetRequest(getReportePostulacionesDashboardAPI({
          ...apiFilterParams,
          searchEvaluacion: debouncedBusquedaEvaluacion
        }));

        const payload = unwrapApiPayload(response);
        let normalizedPayload = payload;

        // Compatibilidad temporal: si el endpoint devuelve lista de evaluaciones, construir estructura dashboard.
        if (Array.isArray(payload)) {
          const filteredItems = filterEvaluacionesByFilters(payload, filtros);
          normalizedPayload = buildDashboardFromEvaluaciones(filteredItems);
        }

        if (!normalizedPayload || typeof normalizedPayload !== 'object' || !normalizedPayload.kpis) {
          throw new Error('Respuesta de dashboard sin estructura esperada');
        }

        if (!isMounted) return;
        setDashboardData(normalizedPayload);
        setDashboardServiceFailed(false);
      } catch (error) {
        if (!isMounted) return;
        setDashboardServiceFailed(true);
      } finally {
        if (isMounted) {
          setLoadingDashboard(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [apiFilterParams, debouncedBusquedaEvaluacion, searchTriggered]);

  useEffect(() => {
    if (!searchTriggered) return;

    let isMounted = true;

    const loadDetalle = async () => {
      setLoadingDetalle(true);
      try {
        const response = await executeGetRequest(getReportePostulacionesDetalleAPI({
          ...apiFilterParams,
          searchEvaluacion: debouncedBusquedaEvaluacion,
          pageNumber: currentPage,
          pageSize: ROWS_PER_PAGE
        }));

        const payload = unwrapApiPayload(response);
        let items = Array.isArray(payload?.items)
          ? payload.items
          : (Array.isArray(payload?.Items) ? payload.Items : null);
        let pagination = payload?.pagination || payload?.Pagination || {};

        // Compatibilidad temporal: si el endpoint devuelve lista de evaluaciones, construir detalle.
        if (!items && Array.isArray(payload)) {
          const filteredItems = filterEvaluacionesByFilters(payload, filtros);
          const mapped = mapEvaluacionesToDetalleItems(filteredItems);
          const start = (currentPage - 1) * ROWS_PER_PAGE;
          const end = start + ROWS_PER_PAGE;
          items = mapped.slice(start, end);
          pagination = {
            totalItems: mapped.length,
            currentPage,
            pageSize: ROWS_PER_PAGE,
            totalPages: Math.max(1, Math.ceil(mapped.length / ROWS_PER_PAGE))
          };
        }

        if (!items) {
          throw new Error('Respuesta de detalle sin items');
        }

        if (!isMounted) return;
        setApiDetalleRows(items.map(mapDetalleItem));
        setApiPagination({
          totalItems: Number(pagination.totalItems || 0),
          currentPage: Number(pagination.currentPage || currentPage),
          pageSize: Number(pagination.pageSize || ROWS_PER_PAGE),
          totalPages: Math.max(1, Number(pagination.totalPages || 1))
        });
        setDetalleServiceFailed(false);
      } catch (error) {
        if (!isMounted) return;
        setDetalleServiceFailed(true);
      } finally {
        if (isMounted) {
          setLoadingDetalle(false);
        }
      }
    };

    loadDetalle();

    return () => {
      isMounted = false;
    };
  }, [apiFilterParams, currentPage, debouncedBusquedaEvaluacion, filtros, searchTriggered]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const mockMetrics = useMemo(() => {
    const total = filteredMockRows.length;
    const aprobados = filteredMockRows.filter((item) => item.resultado === 'Aprobado').length;
    const desaprobados = filteredMockRows.filter((item) => item.resultado === 'Desaprobado').length;
    
    // Calcular cantidad de evaluaciones por tipo
    const tipoMap = filteredMockRows.reduce((acc, item) => {
      acc[item.tipoEvaluacion] = (acc[item.tipoEvaluacion] || 0) + 1;
      return acc;
    }, {});
    const cantidadEvaluaciones = Object.values(tipoMap).reduce((sum, val) => sum + val, 0);

    return {
      total,
      aprobados,
      desaprobados,
      cantidadEvaluaciones,
      aprobacion: total > 0 ? `${Math.round((aprobados / total) * 100)}%` : '0%'
    };
  }, [filteredMockRows]);

  const apiMetrics = useMemo(() => {
    const kpis = dashboardData?.kpis || {};
    const total = Number(kpis.totalPostulaciones || 0);
    const aprobados = Number(kpis.aprobados || 0);
    const desaprobados = Number(kpis.desaprobados || 0);
    const tasa = Number(kpis.tasaAprobacion || 0);
    
    // Calcular cantidad de evaluaciones sumando distribucionPorTipoEvaluacion
    const cantidadEvaluaciones = (dashboardData?.distribucionPorTipoEvaluacion || [])
      .reduce((sum, item) => sum + Number(item?.Cantidad ?? item?.cantidad ?? 0), 0);

    return {
      total,
      aprobados,
      desaprobados,
      cantidadEvaluaciones,
      aprobacion: `${Math.round(tasa)}%`
    };
  }, [dashboardData]);

  const mockByType = useMemo(() => {
    const map = filteredMockRows.reduce((acc, item) => {
      acc[item.tipoEvaluacion] = (acc[item.tipoEvaluacion] || 0) + 1;
      return acc;
    }, {});

    const rows = Object.values(EVAL_TYPES).map((tipo) => ({ label: tipo, value: map[tipo] || 0 }));
    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [filteredMockRows]);

  const apiByType = useMemo(() => {
    const rows = (dashboardData?.distribucionPorTipoEvaluacion || [])
      .map((item) => ({
        label: item?.TipoEvaluacion ?? item?.tipoEvaluacion ?? item?.label,
        value: Number(item?.Cantidad ?? item?.cantidad ?? item?.value ?? 0)
      }))
      .filter((item) => item.label && item.value > 0);

    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [dashboardData]);

  const mockByMonth = useMemo(() => {
    const grouped = {};

    filteredMockRows.forEach((item) => {
      const date = new Date(`${item.fecha}T00:00:00`);
      const month = MONTHS[date.getMonth()];
      grouped[month] = (grouped[month] || 0) + 1;
    });

    const rows = MONTHS.map((month) => ({ label: month, value: grouped[month] || 0 }));
    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [filteredMockRows]);

  const apiByMonth = useMemo(() => {
    const grouped = {};

    (dashboardData?.postulacionesPorMes || []).forEach((item) => {
      const month = resolveApiMonth(item);
      const value = Number(item?.Cantidad ?? item?.cantidad ?? item?.value ?? 0);
      if (month) {
        grouped[month] = (grouped[month] || 0) + value;
      }
    });

    const rows = MONTHS.map((month) => ({ label: month, value: grouped[month] || 0 }));
    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [dashboardData]);

  const mockByAttempts = useMemo(() => {
    const attemptsMap = {};

    filteredMockRows.forEach((item) => {
      const key = item.intentoNum <= 3 ? item.intentoNum : '4+';
      attemptsMap[key] = (attemptsMap[key] || 0) + 1;
    });

    const rows = [
      { label: '1 intento', value: attemptsMap[1] || 0 },
      { label: '2 intentos', value: attemptsMap[2] || 0 },
      { label: '3 intentos', value: attemptsMap[3] || 0 },
      { label: '4+ intentos', value: attemptsMap['4+'] || 0 }
    ];
    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [filteredMockRows]);

  const apiByAttempts = useMemo(() => {
    const grouped = {
      '1 intento': 0,
      '2 intentos': 0,
      '3 intentos': 0,
      '4+ intentos': 0
    };

    (dashboardData?.postulantesPorIntentos || []).forEach((item) => {
      const intento = item?.Intento ?? item?.intento ?? item?.label;
      const value = Number(item?.Cantidad ?? item?.cantidad ?? item?.value ?? 0);
      if (intento && Object.prototype.hasOwnProperty.call(grouped, intento)) {
        grouped[intento] = value;
      }
    });

    const rows = Object.entries(grouped).map(([label, value]) => ({ label, value }));
    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [dashboardData]);

  const mockByEstado = useMemo(() => {
    const estadoMap = filteredMockRows.reduce((acc, item) => {
      acc[item.estadoPostulacion] = (acc[item.estadoPostulacion] || 0) + 1;
      return acc;
    }, {});

    const rows = Object.entries(estadoMap).map(([estado, count]) => ({
      label: estado,
      value: count
    }));
    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [filteredMockRows]);

  const apiByEstado = useMemo(() => {
    const rows = (dashboardData?.postulacionesPorEstado || []).map((item) => ({
      label: item?.EstadoPostulacion ?? item?.estadoPostulacion ?? item?.label ?? 'Sin estado',
      value: Number(item?.Cantidad ?? item?.cantidad ?? item?.value ?? 0)
    }));

    const maxValue = Math.max(...rows.map((item) => item.value), 1);
    return { rows, maxValue };
  }, [dashboardData]);

  const mockApprovedByEvaluation = useMemo(() => {
    const evaluationMap = {};

    filteredMockRows.forEach((item) => {
      if (!evaluationMap[item.nombreEvaluacion]) {
        evaluationMap[item.nombreEvaluacion] = { approved: 0, rejected: 0 };
      }
      if (item.resultado === 'Aprobado') {
        evaluationMap[item.nombreEvaluacion].approved += 1;
      } else {
        evaluationMap[item.nombreEvaluacion].rejected += 1;
      }
    });

    let rows = Object.entries(evaluationMap).map(([nombre, data]) => ({
      label: nombre,
      approved: data.approved,
      rejected: data.rejected
    }));

    if (busquedaEvaluacion.trim()) {
      rows = rows.filter((item) =>
        item.label.toLowerCase().includes(busquedaEvaluacion.toLowerCase())
      );
    }

    const maxValue = Math.max(...rows.map((item) => item.approved + item.rejected), 1);
    return { rows, maxValue };
  }, [filteredMockRows, busquedaEvaluacion]);

  const apiApprovedByEvaluation = useMemo(() => {
    let rows = (dashboardData?.aprobadosVsDesaprobadosPorEvaluacion || []).map((item) => ({
      label: item?.NombreEvaluacion ?? item?.nombreEvaluacion ?? item?.label ?? '-',
      approved: Number(item?.Aprobados ?? item?.aprobados ?? item?.approved ?? 0),
      rejected: Number(item?.Desaprobados ?? item?.desaprobados ?? item?.rejected ?? 0)
    }));

    if (debouncedBusquedaEvaluacion.trim()) {
      rows = rows.filter((item) =>
        item.label.toLowerCase().includes(debouncedBusquedaEvaluacion.toLowerCase())
      );
    }

    const maxValue = Math.max(...rows.map((item) => item.approved + item.rejected), 1);
    return { rows, maxValue };
  }, [dashboardData, debouncedBusquedaEvaluacion]);

  const mockTrendByMonth = useMemo(() => {
    const grouped = filteredMockRows.reduce((acc, item) => {
      const key = monthLabel(item.fecha);
      if (!acc[key]) {
        acc[key] = { total: 0, aprobados: 0 };
      }
      acc[key].total += 1;
      if (item.resultado === 'Aprobado') {
        acc[key].aprobados += 1;
      }
      return acc;
    }, {});

    const sampleData = {
      ene: { percentage: 90 },
      feb: { percentage: 83 },
      mar: { percentage: 93 },
      abr: { percentage: 84 },
      may: { percentage: 87 },
      jun: { percentage: 92 },
      jul: { percentage: 86 },
      ago: { percentage: 92 },
      sep: { percentage: 77 },
      oct: { percentage: 90 },
      nov: { percentage: 97 },
      dic: { percentage: 86 }
    };

    return MONTHS.map((month) => {
      const bucket = grouped[month];
      if (bucket && bucket.total > 0) {
        const value = Math.round((bucket.aprobados / bucket.total) * 100);
        return { label: month, value };
      }
      return { label: month, value: sampleData[month].percentage };
    });
  }, [filteredMockRows]);

  const apiTrendByMonth = useMemo(() => {
    const grouped = {};

    (dashboardData?.tendenciaMensualAprobacion || []).forEach((item) => {
      const month = resolveApiMonth(item);
      const value = Number(item?.AprobacionPct ?? item?.aprobacionPct ?? item?.value ?? 0);
      if (month) {
        grouped[month] = value;
      }
    });

    return MONTHS
      .filter((month) => grouped[month] !== undefined)
      .map((month) => ({
        label: month,
        value: grouped[month]
      }));
  }, [dashboardData]);

  const metrics = shouldUseMockData ? mockMetrics : apiMetrics;
  const byType = shouldUseMockData ? mockByType : apiByType;
  const byMonth = shouldUseMockData ? mockByMonth : apiByMonth;
  const byAttempts = shouldUseMockData ? mockByAttempts : apiByAttempts;
  const byEstado = shouldUseMockData ? mockByEstado : apiByEstado;
  const approvedByEvaluation = shouldUseMockData ? mockApprovedByEvaluation : apiApprovedByEvaluation;
  const trendByMonth = shouldUseMockData ? mockTrendByMonth : apiTrendByMonth;

  const onFilterChange = (event) => {
    const { name, value } = event.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
    setSearchTriggered(false);
  };

  const handleSort = (column, direction) => {
    setSortConfig({ column, direction });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTriggered(true);
  };

  if (showInitialLoader) return <SpinnerLoading />;

  return (
    <>
      <div className="message-no-disponible-en-mobile">
        <p>Reportes no está disponible en mobile.</p>
      </div>

      <div className="app-wrapper">
        <div className="no-disponible-en-mobile">
          <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative' }}>
            <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '130px', overflowY: 'auto', zIndex: 100 }}>
              <SidebarProcesosSeleccion />
            </div>
            <Wrapper className="App" style={{ flex: 1, minWidth: 0, marginLeft: '130px' }}>
              <PageHeader title="Reportes de Postulaciones" />
              <div className="container-fluid body-content rp-container" style={{ borderTop: 'none', padding: '0 36px 48px' }}>
                <section className="rp-top-filters card">
                  <div className="rp-header-copy">
                    <h2>Resultados de Postulaciones</h2>
                    <p>Analiza el desempeño de postulaciones por tipo de evaluación y resultados de aprobación.</p>
                    {shouldUseMockData && (
                      <small className="text-warning">
                        Servicio no disponible. Mostrando data mockeada.
                      </small>
                    )}
                  </div>

                  <div className="rp-filters-grid">
                    <label style={{margin: 0, padding: 0}}>
                      <span>Fecha Inicio</span>
                      <input type="date" name="fechaInicio" value={filtros.fechaInicio} onChange={onFilterChange} />
                    </label>
                    <label style={{margin: 0, padding: 0}}>
                      <span>Fecha Fin</span>
                      <input type="date" name="fechaFin" value={filtros.fechaFin} onChange={onFilterChange} />
                    </label>
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={loadingDashboard || loadingDetalle}
                      className="btn generic-button-buscador"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        alignSelf: 'flex-end',
                        height: 'fit-content',
                        opacity: loadingDashboard || loadingDetalle ? 0.6 : 1,
                        cursor: loadingDashboard || loadingDetalle ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {(loadingDashboard || loadingDetalle) ? (
                        <>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '14px',
                              height: '14px',
                              border: '2px solid rgba(255,255,255,0.3)',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 0.8s linear infinite'
                            }}
                          />
                          Cargando...
                        </>
                      ) : (
                        'BUSCAR'
                      )}
                    </button>
                  </div>
                  <style>{`
                    @keyframes spin {
                      to { transform: rotate(360deg); }
                    }
                  `}</style>
                </section>

                <section className="rp-kpi-grid">
                  <DashboardCard title={CARD_CONFIG[0].title} value={metrics.total} hint="Registros después de filtros" accent={CARD_CONFIG[0].accent} />
                  <DashboardCard title={CARD_CONFIG[1].title} value={metrics.aprobacion} hint={`${metrics.aprobados} aprobados`} accent={CARD_CONFIG[1].accent} />
                  <DashboardCard title={CARD_CONFIG[2].title} value={metrics.desaprobados} hint={`${metrics.desaprobados} desaprobados`} accent={CARD_CONFIG[2].accent} />
                  <DashboardCard title={CARD_CONFIG[3].title} value={metrics.cantidadEvaluaciones} hint="Total de evaluaciones" accent={CARD_CONFIG[3].accent} />
                </section>

                <section className="rp-charts-grid">
                  <article className="rp-chart-card">
                    <h3>Postulaciones por Mes</h3>
                    <SimpleBarChart data={byMonth.rows} maxValue={byMonth.maxValue} colorClass="blue" />
                  </article>

                  <article className="rp-chart-card">
                    <h3>Distribución por Tipo Evaluación</h3>
                    <PieChart data={byType.rows} />
                  </article>

                  <article className="rp-chart-card">
                    <h3>Postulantes por Numero Intentos</h3>
                    <SimpleBarChart data={byAttempts.rows} maxValue={byAttempts.maxValue} colorClass="cyan" />
                  </article>

                  <article className="rp-chart-card">
                    <h3>Postulaciones por Estado</h3>
                    <SimpleBarChart data={byEstado.rows} maxValue={byEstado.maxValue} colorClass="orange" />
                  </article>

                  <article className="rp-chart-card">
                    <div className="">
                      <h3 className="">Aprobados vs Desaprobados por Evaluación</h3>
                      <input
                        type="text"
                        placeholder="Buscar evaluación..."
                        value={busquedaEvaluacion}
                        onChange={(e) => setBusquedaEvaluacion(e.target.value)}
                        className="rp-eval-search"
                      />
                    </div>
                    {approvedByEvaluation.rows.length > 0 ? (
                      <StackedBarChart data={approvedByEvaluation.rows} />
                    ) : (
                      <div className="rp-no-data">No hay evaluaciones que coincidan con la búsqueda</div>
                    )}
                  </article>

                  <article className="rp-chart-card">
                    <h3>Tendencia Mensual de Aprobación (%)</h3>
                    <TinyLineChart data={trendByMonth} />
                  </article>
                </section>

                <section className="card rp-results-card">
                  <div className="rp-results-header">
                    <h3>Detalle de Evaluaciones</h3>
                    <p>{totalResults} resultados</p>
                  </div>

                  {(loadingDashboard || loadingDetalle) && !shouldUseMockData ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Cargando...</span>
                      </div>
                      <div className="mt-2">Cargando reportes...</div>
                    </div>
                  ) : (
                    <Table rows={tableRows} sortConfig={sortConfig} onSort={handleSort} />
                  )}

                  <div className="rp-pagination-wrap">
                    <button
                      type="button"
                      className="rp-page-btn"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </button>

                    <div className="rp-page-numbers">
                      {visiblePages.map((page) => (
                        <button
                          key={page}
                          type="button"
                          className={`rp-page-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="rp-page-btn"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </button>
                  </div>
                </section>
              </div>
            </Wrapper>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportesPostulacionesDashboard;
